import dayjs from "dayjs";
import type { GanttReport } from "../../../queries/reports";
import { PROJECT_STATUS, SPRINT_STATUS } from "../../ui/StatusTag";
import { ganttMarker, ganttPosition } from "./format";
import styles from "./dash.module.css";

interface Item {
  id: string;
  name: string;
  kind: "project" | "sprint";
  statusLabel: string;
  start: string;
  end: string;
  detail: string;
  muted: boolean;
}

export function ganttItems(report: GanttReport): Item[] {
  const projects: Item[] = report.projects
    .filter((p) => p.startDate && p.targetDate)
    .map((p) => ({
      id: p.projectId, name: p.name, kind: "project" as const, statusLabel: PROJECT_STATUS[p.status].label,
      start: p.startDate as string, end: p.targetDate as string, detail: "Project",
      muted: p.status === "archived" || p.status === "completed",
    }));
  const sprints: Item[] = report.sprints.map((s) => ({
    id: s.sprintId, name: s.name, kind: "sprint" as const, statusLabel: SPRINT_STATUS[s.status].label,
    start: s.startDate, end: s.endDate, detail: `Sprint, ${s.taskCount} tasks`, muted: s.status === "completed",
  }));
  return [...projects, ...sprints];
}

/** Project (start to target) and sprint (start to end) bars on one time axis, with a today marker. */
export const GanttBars = ({ report, now = Date.now() }: { report: GanttReport; now?: number }) => {
  const items = ganttItems(report);
  const day = 86400000;
  const all = items.flatMap((i) => [+new Date(i.start), +new Date(i.end)]).concat(now);
  const dStart = Math.min(...all) - 3 * day;
  const dEnd = Math.max(...all) + 3 * day;
  const today = ganttMarker(now, dStart, dEnd);

  // month ticks along the axis
  const ticks: { left: number; label: string }[] = [];
  let m = dayjs(dStart).startOf("month").add(1, "month");
  while (+m < dEnd) {
    ticks.push({ left: ((+m - dStart) / (dEnd - dStart)) * 100, label: m.format("MMM YYYY") });
    m = m.add(1, "month");
  }
  const skip = Math.ceil(ticks.length / 8);

  const row = (i: Item) => {
    const pos = ganttPosition(i.start, i.end, dStart, dEnd);
    return (
      <li key={`${i.kind}-${i.id}`} className={styles.ganttRow}>
        <div className={styles.ganttLabel}>
          <span className={styles.ganttName}>{i.name}</span>
          <span className={styles.muted}>{i.statusLabel}</span>
        </div>
        <div className={styles.ganttTrack}>
          {today !== null && <span className={styles.today} style={{ left: `${today}%` }} aria-hidden="true" />}
          <span
            className={`${styles.ganttBar} ${i.kind === "sprint" ? styles.ganttSprint : styles.ganttProject} ${i.muted ? styles.ganttMuted : ""}`}
            style={{ left: `${pos.left}%`, width: `${pos.width}%` }}
            title={`${i.detail}: ${dayjs(i.start).format("D MMM YYYY")} to ${dayjs(i.end).format("D MMM YYYY")}`}
          />
          <span className={styles.srOnly}>{`${i.detail}, ${dayjs(i.start).format("D MMM YYYY")} to ${dayjs(i.end).format("D MMM YYYY")}`}</span>
        </div>
      </li>
    );
  };
  const projects = items.filter((i) => i.kind === "project");
  const sprints = items.filter((i) => i.kind === "sprint");

  return (
    <div className={styles.tableWrap}>
      <div className={styles.gantt}>
        <div className={styles.ganttAxis}>
          <span />
          <div className={styles.ganttAxisTrack}>
            {ticks.map((t, idx) => idx % skip === 0 && (
              <span key={t.label} className={styles.axis} style={{ left: `${t.left}%` }}>{t.label}</span>
            ))}
            {today !== null && <span className={styles.todayLabel} style={{ left: `${today}%` }}>Today</span>}
          </div>
        </div>
        {projects.length > 0 && <h3 className={styles.ganttGroup}>Projects</h3>}
        <ul className={styles.plainList}>{projects.map(row)}</ul>
        {sprints.length > 0 && <h3 className={styles.ganttGroup}>Sprints</h3>}
        <ul className={styles.plainList}>{sprints.map(row)}</ul>
      </div>
    </div>
  );
};
